import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
// Map temporarily disabled - kept commented for future re-enable
// import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Card, Row, Col, Typography, Tag, Select, Space, Input } from "antd";
import {
  GlobalOutlined, EnvironmentOutlined, TeamOutlined,
  FlagOutlined, SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { fetchCifSummary } from "../../utils/cifApi";
import { fetchOicCountries, getFlagUrl } from "../../utils/countriesApi";
import "./CifHome.scss";

// Unified navy color for all CIF regions
const { Text, Paragraph } = Typography;

// const GEO_URL = "/countries-50m.json";
const OIC_BLUE = "#2F83C5";
// const NON_OIC_COLOR = "#E0E0E0";
// const NON_OIC_STROKE = "transparent";
// const SEA_COLOR = "#d4e6f5";

const STAT_CARDS = [
  { icon: <FlagOutlined />, bg: "#2F83C5" },
  { icon: <TeamOutlined />, bg: "#2F83C5" },
  { icon: <EnvironmentOutlined />, bg: "#2F83C5" },
];

function buildSummaryLookup(summaryList) {
  const lookup = {};
  if (summaryList) {
    summaryList.forEach((item) => {
      lookup[item.c_code] = item;
    });
  }
  return lookup;
}

function parseArea(str) {
  if (!str) return 0;
  const cleaned = str.replace(/[^0-9.]/g, '');
  return parseFloat(cleaned) || 0;
}

function formatArea(data) {
  if (!data) return "—";
  const total = data.reduce((sum, item) => sum + parseArea(item.total_area), 0);
  if (total === 0) return "—";
  if (total >= 1e6) return (total / 1e6).toFixed(1) + "M km²";
  if (total >= 1e3) return (total / 1e3).toFixed(1) + "K km²";
  return total.toLocaleString("en-US") + " km²";
}

function formatPopulation(value) {
  if (value === null || value === undefined || value === 0) return null;
  if (value >= 1e9) return (value / 1e9).toFixed(2) + "B";
  if (value >= 1e6) return (value / 1e6).toFixed(1) + "M";
  if (value >= 1e3) return (value / 1e3).toFixed(1) + "K";
  return Math.round(value).toLocaleString("en-US");
}

export default function CifHome() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [oicData, setOicData] = useState({ byIsoNum: {}, isoNumSet: new Set() });
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [searchText, setSearchText] = useState("");
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataVersion, setDataVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchCifSummary(),
      fetchOicCountries(),
    ]).then(([summary, countries]) => {
      if (!cancelled) {
        setSummaryData(summary);
        setOicData({
          byIsoNum: Object.fromEntries(countries.map((c) => [c.iso_num, c])),
          isoNumSet: new Set(countries.map((c) => c.iso_num)),
        });
        setLoading(false);
        // Bump after the translated data has been applied and the list has
        // re-sorted, so the entry animation plays on the settled order.
        setDataVersion((v) => v + 1);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [i18n.language]);

  const summaryLookup = React.useMemo(() => buildSummaryLookup(summaryData), [summaryData]);

  const cifCountries = summaryData
    ? summaryData.filter((item) => item.capital).length
    : 0;

  const visibleCountries = React.useMemo(() => {
    const all = Object.values(oicData.byIsoNum || {});
    const filteredBySearch = all.filter(c => !searchText || c.name.toLowerCase().includes(searchText.toLowerCase()));
    return filteredBySearch.sort((a, b) => a.name.localeCompare(b.name));
  }, [oicData, searchText]);

  // Section cards (shared by both rows) built from translations
  const profileSections = React.useMemo(() =>
    Array.from({ length: 7 }, (_, idx) => ({
      num: String(idx + 1),
      title: t(`cif.sections.${idx + 1}.title`),
      desc: t(`cif.sections.${idx + 1}.desc`),
      color: "#2F83C5",
    })),
    [t]
  );

  // Map temporarily disabled - helpers kept commented for future re-enable
  // const handleCountryClick = (geo) => {
  //   const country = oicData.byIsoNum[geo.id];
  //   if (country) {
  //     navigate(`/cif/${country.c_code}`);
  //   }
  // };

  // Shared tooltip, used by the member list cards (and previously the map)
  const showTooltip = (country, evt) => {
    const cif = summaryLookup[country.c_code];
    setTooltipContent({
      name: country.name,
      c_code: country.c_code,
      iso_a2: country.iso_a2,
      capital: cif?.capital || "N/A",
      area: cif?.total_area || null,
      currency: cif?.currency_unit || null,
      population: cif?.population || null,
      gdpPerCapita: cif?.gdp_per_capita ?? null,
    });
    // Anchor the tooltip to the top-right of the hovered box, not the mouse
    const rect = evt.currentTarget.getBoundingClientRect();
    // Shift further right of the box, clamped so it never leaves the screen
    setTooltipPosition({ x: Math.max(window.innerWidth - rect.right - 140, 16), y: rect.top });
  };

  const handleMouseLeave = () => {
    setTooltipContent(null);
  };

  // const handleMouseEnter = (geo, evt) => {
  //   const country = oicData.byIsoNum[geo.id];
  //   if (country) {
  //     showTooltip(country, evt);
  //   }
  // };

  // const isOicCountry = (geoId) => oicData.isoNumSet.has(geoId);

  // const getCountryColor = (geoId) => {
  //   if (!isOicCountry(geoId)) return NON_OIC_COLOR;
  //   const country = oicData.byIsoNum[geoId];
  //   if (selectedRegion !== "all" && country && country.region !== selectedRegion) {
  //     return "#90CAF9";
  //   }
  //   return OIC_BLUE;
  // };

  // const getCountryStroke = (geoId) => {
  //   if (!isOicCountry(geoId)) return NON_OIC_STROKE;
  //   return getCountryColor(geoId);
  // };

  // const getCountryStrokeWidth = (geoId) => {
  //   return isOicCountry(geoId) ? 1.5 : 0;
  // };

  // // Somaliland (-99) → Somalia (706), Western Sahara (732) → Mauritania (478)
  // const resolveGeoId = (geo) => {
  //   if (geo.id === "-99" || geo.properties?.name === "Somaliland") return "706";
  //   if (geo.id === "732") return "478";
  //   return geo.id;
  // };

  return (
    <div className="cif-home">
      {loading ? (
        <div className="cif-home__loading">
          <div className="cif-home__skel-stat-row">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="cif-home__skel-stat"
                style={{ animationDelay: `${i * 0.1}s` }}
              />
            ))}
          </div>
          <div className="cif-home__skel-map" />
          <div className="cif-home__skel-info" />
          <div className="cif-home__skel-list">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="cif-home__skel-country" />
            ))}
          </div>
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]} className="cif-home__stat-row">
            {[
              { label: t("cif.home.memberStates"), value: cifCountries, idx: 0 },
              { label: t("cif.home.totalPopulation"), value: (() => { const oicCodes = new Set(Object.values(oicData.byIsoNum).map(c => c.c_code)); const total = summaryData.filter(i => oicCodes.has(i.c_code)).reduce((s, i) => s + (i.population || 0), 0); return total > 0 ? formatPopulation(total) : "—"; })(), idx: 1 },
              { label: t("cif.home.totalArea"), value: (() => { const oicCodes = new Set(Object.values(oicData.byIsoNum).map(c => c.c_code)); return formatArea(summaryData.filter(i => oicCodes.has(i.c_code))); })(), idx: 2 },
            ].map((card) => (
              <Col xs={8} key={card.idx}>
                <Card
                  size="small"
                  styles={{ body: { padding: "14px" } }}
                  style={{
                    background: STAT_CARDS[card.idx].bg,
                    animationDelay: `${card.idx * 0.08}s`,
                  }}
                  className="cif-home__stat-card cif-home__animate-in"
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div className="cif-home__stat-icon-wrap">
                      {STAT_CARDS[card.idx].icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text className="cif-home__stat-label">{card.label}</Text>
                      <Text strong className="cif-home__stat-value">{card.value}</Text>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* MAP temporarily disabled - OIC Members list moved to the map area */}
          {/*
          <div className="cif-home__map-wrapper cif-home__animate-in" style={{ animationDelay: '0.25s' }}>
            <div>
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ scale: 350, center: [30, 19] }}
                style={{ width: "100%", height: 380, background: SEA_COLOR }}
              >
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const effectiveId = resolveGeoId(geo);
                    const isOic = !!oicData.byIsoNum[effectiveId];
                    if (geo.id === "010") return null;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onClick={() => isOic && handleCountryClick({ ...geo, id: effectiveId })}
                        onMouseEnter={(evt) => {
                          if (isOic) handleMouseEnter({ ...geo, id: effectiveId }, evt);
                        }}
                        onMouseLeave={handleMouseLeave}
                        style={{
                          default: {
                            fill: getCountryColor(effectiveId),
                            stroke: getCountryStroke(effectiveId),
                            strokeWidth: getCountryStrokeWidth(effectiveId),
                            outline: "none",
                          },
                          hover: {
                            fill: getCountryColor(effectiveId),
                            stroke: getCountryStroke(effectiveId),
                            strokeWidth: getCountryStrokeWidth(effectiveId),
                            outline: "none",
                            cursor: isOic ? "pointer" : "default",
                          },
                          pressed: {
                            fill: getCountryColor(effectiveId),
                            stroke: getCountryColor(effectiveId),
                            strokeWidth: getCountryStrokeWidth(effectiveId),
                            outline: "none",
                          },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>

            <div className="cif-home__map-legend">
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span className="cif-home__legend-dot" style={{ background: OIC_BLUE }} />
                <span>OIC Member State</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span className="cif-home__legend-dot" style={{ background: NON_OIC_COLOR }} />
                <span>Non-OIC Country</span>
              </div>
            </div>

            <div className="cif-home__map-legend-region">
              <Text strong style={{ fontSize: 11 }}>OIC by Region</Text>
              {oicRegions.map(r => (
                <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                  <span className="cif-home__region-dot" style={{ background: r.color }} />
                  <span>{r.key}</span>
                  <Tag style={{ marginLeft: "auto", fontSize: 10, lineHeight: "16px" }}>
                    {Object.values(oicData.byIsoNum).filter(c => c.region === r.key).length}
                  </Tag>
                </div>
              ))}
            </div>
          </div>
          */}

          <Card
            className="cif-home__list-card cif-home__animate-in"
            style={{ animationDelay: '0.25s', marginBottom: 16 }}
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div className="cif-home__list-title-icon"><EnvironmentOutlined /></div>
                <div>
                  <Text strong className="cif-home__list-title-text">{t("cif.home.memberStatesList")}</Text>
                  <Text className="cif-home__list-subtitle">
                    {visibleCountries.length} {t("cif.home.countries", { count: visibleCountries.length })}
                    {searchText ? ` ${t("cif.home.matching")} "${searchText}"` : ""}
                  </Text>
                </div>
              </div>
            }
            extra={
              <Input
                size="middle"
                placeholder={t("cif.home.searchPlaceholder")}
                prefix={<SearchOutlined style={{ color: "#999" }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 220, maxWidth: '220px', borderRadius: 20, paddingLeft: 12, boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.04)', flex: '0 0 220px' }}
                allowClear
              />
            }
          >
            <Row gutter={[12, 12]} key={dataVersion}>
              {Object.values(oicData.byIsoNum)
                .filter(c => !searchText || c.name.toLowerCase().includes(searchText.toLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((country, idx) => {
                  const cif = summaryLookup[country.c_code];
                  return (
                    <Col xs={12} sm={8} md={8} lg={6} xl={4} key={country.c_code}>
                      <Card
                        hoverable
                        onClick={() => navigate(`/cif/${country.c_code}`)}
                        onMouseEnter={(evt) => showTooltip(country, evt)}
                        onMouseLeave={handleMouseLeave}
                        className="cif-home__country-card"
                        style={{ animationDelay: `${idx * 25}ms` }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ flexShrink: 0 }}>
                            {country.iso_a2 ? (
                              <img
                                src={getFlagUrl(country.c_code)}
                                alt=""
                                style={{ width: 42, height: "auto", borderRadius: 4, display: "block" }}
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            ) : (
                              <div className="cif-home__country-dot" style={{ background: OIC_BLUE }} />
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text strong className="cif-home__country-name">{country.name}</Text>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  );
                })}
            </Row>
          </Card>

          <div
            className={`cif-home__tooltip${tooltipContent ? ' cif-home__tooltip--visible' : ''}`}
            style={{ right: tooltipPosition.x, top: tooltipPosition.y - 170 }}
          >
            {tooltipContent && (
              <>
                <div className="cif-home__tt-header">
                  {tooltipContent.iso_a2 && (
                    <img
                      className="cif-home__tt-flag"
                      src={getFlagUrl(tooltipContent.c_code)}
                      alt=""
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  )}
                  <div className="cif-home__tt-header-text">
                    <span className="cif-home__tt-name">{tooltipContent.name}</span>
                  </div>
                </div>
                <div className="cif-home__tt-stats">
                  <div className="cif-home__tt-stat">
                    <span className="cif-home__tt-stat-label">{t("cif.home.capital")}</span>
                    <span className="cif-home__tt-stat-value">{tooltipContent.capital}</span>
                  </div>
                  {tooltipContent.population && (
                    <div className="cif-home__tt-stat">
                      <span className="cif-home__tt-stat-label">{t("cif.home.population")}</span>
                      <span className="cif-home__tt-stat-value">{formatPopulation(tooltipContent.population)}</span>
                    </div>
                  )}
                  {tooltipContent.area && (
                    <div className="cif-home__tt-stat">
                      <span className="cif-home__tt-stat-label">{t("cif.home.totalArea")}</span>
                      <span className="cif-home__tt-stat-value">{tooltipContent.area}</span>
                    </div>
                  )}
                  {tooltipContent.gdpPerCapita != null && (
                    <div className="cif-home__tt-stat">
                      <span className="cif-home__tt-stat-label">{t("cif.home.gdpPerCapita")}</span>
                      <span className="cif-home__tt-stat-value">{Math.round(tooltipContent.gdpPerCapita).toLocaleString("en-US")} USD</span>
                    </div>
                  )}
                  {tooltipContent.currency && (
                    <div className="cif-home__tt-stat">
                      <span className="cif-home__tt-stat-label">{t("cif.home.currency")}</span>
                      <span className="cif-home__tt-stat-value">{tooltipContent.currency}</span>
                    </div>
                  )}
                </div>
                <div className="cif-home__tt-footer">{t("cif.home.clickToProfile")}</div>
                <div className="cif-home__tt-arrow" />
              </>
            )}
          </div>

          <Card
            size="small"
            className="cif-home__info-card cif-home__animate-in"
            style={{ animationDelay: '0.4s' }}
          >
            <Row justify="center" style={{ marginBottom: 14 }}>
              <Col xs={24} md={20} lg={16}>
                <div style={{ textAlign: "center" }}>
                  <Text strong className="cif-home__about-title">{t("cif.home.aboutTitle")}</Text>
                  <Paragraph className="cif-home__about-desc">
                    <strong>{t("cif.title")}</strong> {t("cif.home.aboutIntro")}{" "}
                    <a href="https://www.oic-oci.org/en/members" target="_blank" rel="noopener noreferrer">{t("cif.home.memberStatesLink")}</a>.
                  </Paragraph>
                  <Paragraph className="cif-home__about-desc">
                    {t("cif.home.aboutGuide")}
                  </Paragraph>
                </div>
              </Col>
            </Row>

            <div className="cif-home__sections-divider">
              <Text className="cif-home__sections-label">{t("cif.home.sectionsLabel")}</Text>
              <Row gutter={[10, 10]}>
                {profileSections.slice(0, 4).map((s) => (
                  <Col key={s.num} xs={24} sm={12} md={12} lg={6}>
                    <div className="cif-home__section-card">
                      <div className="cif-home__section-num" style={{ background: s.color }}>{s.num}</div>
                      <div className="cif-home__section-text">
                        <Text className="cif-home__section-title">{s.title}</Text>
                        <Text className="cif-home__section-desc">{s.desc}</Text>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
              <Row gutter={[10, 10]} style={{ marginTop: 16 }}>
                {profileSections.slice(4).map((s) => (
                  <Col key={s.num} xs={24} sm={12} md={8} lg={8}>
                    <div className="cif-home__section-card">
                      <div className="cif-home__section-num" style={{ background: s.color }}>{s.num}</div>
                      <div className="cif-home__section-text">
                        <Text className="cif-home__section-title">{s.title}</Text>
                        <Text className="cif-home__section-desc">{s.desc}</Text>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
